import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import { VisitSettlement, VisitSettlementQuery } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { PurchaseId } from '@dreryk/egabinet-financial-settlement-published-language-api';
import { CurrentHealthcareProviderInvoiceIntegrationEnabledQuery } from '@dreryk/egabinet-invoice-frontend-api';

import { useCqrsFind, useCqrsGet } from '@dreryk/command-query-react-common';
import {
   dispatchSnackbarNegativeEvent,
   EmptyStatePlaceholder,
   LoaderSpinner,
   RetryButton,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { AdjustmentByFormBean } from '../../shared/form/adjustment/adjustment.types';
import { createAdjustmentByFormBean } from '../../shared/form/adjustment/adjustment.utils';
import { mapToVisitMedicalServicesTableBean } from '../../shared/medicalServices/medicalServices.utils';
import { VisitMedicalServiceInteractiveTable } from '../../shared/medicalServices/VisitMedicalServiceInteractiveTable';
import { visitSettlementTranslationKeys } from '../../translations';
import {
   VisitSettlementControl,
   VisitSettlementController,
   VisitSettlementRegister,
} from '../../visitSettlement.types';
import { getVisitSettlementMedicalServicesValue, getVisitSettlementValue } from '../../visitSettlement.utils';

import { StyledVisitSettlementFormTab } from './VisitSettlementFormTab.styled';
import { VisitSettlementFormTabInteractiveTableBottomElement } from './VisitSettlementFormTabInteractiveTableBottomElement';

const t = visitSettlementTranslationKeys.extensions.visitForm.formTab;

type VisitSettlementFormTabLoaderRenderProps = {
   adjustmentBy: AdjustmentByFormBean | undefined;
   invoiceProviderIntegrationEnabled: boolean;
};

type VisitSettlementFormTabLoaderRenderer = (renderProps: VisitSettlementFormTabLoaderRenderProps) => JSX.Element;

type VisitSettlementFormTabLoaderProps = {
   readonly appointmentId: number;
   readonly children: VisitSettlementFormTabLoaderRenderer;
   readonly controller: VisitSettlementController;
};

export const VisitSettlementFormTabLoader = (props: VisitSettlementFormTabLoaderProps): JSX.Element | null => {
   const invoiceCreateAvailable = protectFeature('creatingInvoices');
   const privateMedicalServicesReadOnlyAvailable = protectFeature('privateMedicalServicesReadOnly');

   const {
      data: invoiceProviderIntegrationEnabled,
      loading: invoiceProviderIntegrationEnabledLoading,
      error: invoiceProviderIntegrationEnabledError,
   } = useCqrsGet<boolean>(() => new CurrentHealthcareProviderInvoiceIntegrationEnabledQuery(), {
      enabled: invoiceCreateAvailable,
   });

   const {
      data: visitSettlement,
      error: visitSettlementError,
      loading: visitSettlementLoading,
      refetch: refetchVisitSettlement,
   } = useCqrsFind<VisitSettlement>(
      () => {
         const query = new VisitSettlementQuery();
         query.purchaseIds = [PurchaseId.forAppointment(AppointmentId.of(props.appointmentId))];
         return query;
      },

      {
         onSuccess: (result) => {
            props.controller.sync({
               visitSettlement: {
                  id: result?.id,
                  editable:
                     !privateMedicalServicesReadOnlyAvailable &&
                     ((result && result.settlementStatus === 'UNSETTLED') ?? true),
                  medicalServices: result?.medicalServices.map(mapToVisitMedicalServicesTableBean) ?? [],
                  priceChangeReason: result?.priceChangeReason ?? null,
                  advancePayments: result?.advancePayments,
                  ownedByCurrentUser: true,
                  medicalServicesSearch: null,
               },
            });
         },
         onUnexpectedError: () => {
            dispatchSnackbarNegativeEvent(t.errors.unexpectedError);
         },
      }
   );

   if (visitSettlementLoading || invoiceProviderIntegrationEnabledLoading) {
      return <LoaderSpinner />;
   }

   if (visitSettlementError || invoiceProviderIntegrationEnabledError) {
      return (
         <EmptyStatePlaceholder
            heading={t.errors.couldNotFetchMedicalServices}
            testId={'couldNotFetchMedicalServices'}
            fullLength
         >
            <RetryButton onClick={refetchVisitSettlement} testId={'retry'} />
         </EmptyStatePlaceholder>
      );
   }
   return props.children({
      invoiceProviderIntegrationEnabled: invoiceProviderIntegrationEnabled,
      adjustmentBy: createAdjustmentByFormBean({
         adjustment: visitSettlement?.adjustment,
      }),
   });
};

type VisitSettlementFormTabProps = {
   readonly control: VisitSettlementControl;
   readonly register: VisitSettlementRegister;
   readonly practitionerId: number;
   readonly adjustmentBy: AdjustmentByFormBean | undefined;
   readonly appointmentId: number;
};

export const VisitSettlementFormTab = (props: VisitSettlementFormTabProps): JSX.Element | null => {
   const visitSettlement = getVisitSettlementValue(props.control);
   const medicalServices = getVisitSettlementMedicalServicesValue(props.control);

   if (!visitSettlement) {
      return null;
   }

   return (
      <StyledVisitSettlementFormTab>
         <VisitMedicalServiceInteractiveTable
            register={props.register}
            control={props.control}
            practitionerId={props.practitionerId}
            bottomElement={
               <VisitSettlementFormTabInteractiveTableBottomElement
                  adjustmentBy={props.adjustmentBy}
                  editable={visitSettlement.editable}
                  medicalServices={medicalServices}
                  appointmentId={props.appointmentId}
                  control={props.control}
               />
            }
         />
      </StyledVisitSettlementFormTab>
   );
};
